import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
    ConfirmationService,
    MessageService
} from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { DialogService } from 'primeng/dynamicdialog';
import { Subject } from 'rxjs';

import { AuthProvider } from '@/auth/enums';
import { AuthService } from '@/auth/services';
import { DataTableComponent } from '@/components';
import {
    DataTableColumnType,
    FilterType,
    UserRole,
    UserStatus
} from '@/enums';
import {
    DataTableColumn,
    User
} from '@/interfaces';
import { UserViewComponent } from '@/pages';
import { UserCreateComponent } from '@/pages/users/create/create.component';
import { UsersService } from '@/service';

@Component({
    selector: 'app-user-listing',
    standalone: true,
    templateUrl: 'listing.component.html',
    imports: [
        DataTableComponent,
        FormsModule,
        ConfirmDialog
    ],
    providers: [ConfirmationService, DialogService]
})
export class UserListingComponent {

    columns: DataTableColumn<User>[] = [
        {
            field: 'name',
            header: 'Name',
            type: DataTableColumnType.MODAL,
            modalComponent: UserViewComponent,
            modalTitle: 'User Details',
            modalData: (row: User) => ({ user: row }),
            frozen: true
        },
        {
            field: 'email',
            header: 'Email',
            type: DataTableColumnType.EMAIL
        },
        {
            field: 'role',
            header: 'Role',
            type: DataTableColumnType.ROLE,
            filterType: FilterType.MULTISELECT,
            filterOptions: Object.values(UserRole).map(value => ({ value, label: value }))
        },
        {
            field: 'status',
            header: 'Status',
            type: DataTableColumnType.STATUS,
            filterType: FilterType.SELECT,
            filterOptions: Object.values(UserStatus).map(value => ({ value, label: value }))
        },
        {
            field: 'phone',
            header: 'Phone',
            type: DataTableColumnType.PHONE
        },
        {
            field: 'auth_provider',
            header: 'Auth Provider',
            filterType: FilterType.SELECT,
            filterOptions: Object.values(AuthProvider).map(value => ({ value, label: value }))
        },
        {
            field: 'created_at',
            header: 'Created At',
            type: DataTableColumnType.DATETIME,
            filterType: FilterType.DATE
        },
        {
            field: 'last_login',
            header: 'Last Login',
            type: DataTableColumnType.DATETIME,
            filterType: FilterType.DATE
        },
        {
            field: 'actions',
            header: '',
            type: DataTableColumnType.ACTIONS,
            sortable: false,
            filterable: false,
            actions: [
                {
                    icon: 'pi pi-pencil',
                    label: 'Edit',
                    command: (row: User) => this.openCreateModal(row),
                    showIf: (row: User) => this.authService.hasPermission('auth.users.update') && row.id !== this.authService.user?.id
                },
                {
                    icon: 'pi pi-trash',
                    label: 'Delete',
                    showIf: (row: User) => row.status !== UserStatus.INACTIVE && this.authService.hasPermission('auth.users.delete') && row.id !== this.authService.user?.id,
                    command: (row: User) => this.delete(row)
                },
                {
                    icon: 'pi pi-refresh',
                    label: 'Restore',
                    command: (row: User) => this.edit(row, { status: UserStatus.ACTIVE }),
                    showIf: (row: User) => row.status === UserStatus.INACTIVE && this.authService.hasPermission('auth.users.update') && row.id !== this.authService.user?.id
                }
            ]
        }
    ];
    loading = false;
    reloadTable$ = new Subject<void>();

    constructor(
        public authService: AuthService,
        private usersService: UsersService,
        private confirmationService: ConfirmationService,
        private messageService: MessageService,
        private dialogService: DialogService
    ) {
    }

    loadData = (params: Record<string, any>) => this.usersService.listing(params);

    exportData = (params: Record<string, any>) => this.usersService.export(params);

    edit(user: User, data: Record<string, any>): void {
        this.loading = true;
        this.usersService.edit(user.id, data).subscribe({
            next: () => {
                this.loading = false;
                this.messageService.add({
                    severity: 'success',
                    detail: 'User updated successfully.'
                });
                this.reloadTable$.next();
            },
            error: (err) => {
                this.loading = false;
                if (err.error?.error) {
                    this.messageService.add({
                        severity: 'error',
                        detail: err.error?.error
                    });
                }
            }
        });
    }

    delete(user: User): void {
        this.confirmationService.confirm({
            header: 'Delete',
            message: `Are you sure you want to delete ${user?.name}?`,
            accept: () => {
                this.loading = true;
                this.usersService.delete(user.id).subscribe({
                    next: () => {
                        this.loading = false;
                        this.messageService.add({
                            severity: 'success',
                            detail: 'User deleted successfully.'
                        });
                        this.reloadTable$.next();
                    },
                    error: (err) => {
                        this.loading = false;
                        if (err.error?.error) {
                            this.messageService.add({
                                severity: 'error',
                                detail: err.error?.error
                            });
                        }
                    }
                });
            }
        });
    }

    openCreateModal(data: Record<string, any> = {}): void {
        this.dialogService.open(UserCreateComponent, {
            header: data['id'] ? 'Edit User' : 'Create User',
            width: '50vw',
            data,
            dismissableMask: true,
            closable: true
        }).onClose.subscribe(result => {
            if (result) {
                this.reloadTable$.next();
            }
        });
    }
}
